import React, { useCallback, useEffect, useState } from "react";

import GoogleMapReact from "google-map-react";

import { getDefaultSquare, getGoogleKey, getZoomLevel, updateMapFooterStyles } from "utils";

export const MapInteractiveWithBoundedOverlay = ({
  defaultZoom, // overrides default if defined
  disableDrag, // sets the drag option to false
  field, // should be used for maps with single shape
  geometry, // should be used for maps with single shape
  location,
  onChange,
  scaleControl = true, // sets scale control to true
  zoomControl = true, // sets zoom control to true
  overlay = null, // sets an image overlay
}) => {
  const [map, setMap] = useState(null);
  const [maps, setMaps] = useState(null);
  const [zoom, setZoom] = useState(15);
  const [coordinates, setCoordinates] = useState({
    lat: 52.5224832,
    lng: 13.3906564,
    default: true,
  });

  const getMapOptions = (maps) => ({
    streetViewControl: false,
    keyboardShortcuts: false,
    scaleControl: scaleControl,
    fullscreenControl: false,
    draggable: !disableDrag,
    rotateControl: false,
    styles: [
      {
        featureType: "poi.business",
        elementType: "labels",
        stylers: [
          {
            visibility: "off",
          },
        ],
      },
    ],
    gestureHandling: "cooperative",
    disableDoubleClickZoom: true,
    mapTypeControl: false,
    mapTypeId: maps.MapTypeId.HYBRID,
    mapTypeControlOptions: {
      style: maps.MapTypeControlStyle.HORIZONTAL_BAR,
      position: maps.ControlPosition.BOTTOM_CENTER,
      mapTypeIds: [maps.MapTypeId.ROADMAP, maps.MapTypeId.SATELLITE, maps.MapTypeId.HYBRID],
    },
    zoomControl: zoomControl,
    clickableIcons: false,
  });

  const polygonStyling = (data) => ({
    strokeColor: "#ffffff",
    strokeOpacity: data.strokeOpacity ? data.strokeOpacity : 0.8,
    strokeWeight: data.strokeWeight ? data.strokeWeight : 2,
    // fillColor: data.red ? "#db3804" : "#ffffff",
    fillOpacity: 0,
    paths: data.path,
    draggable: false,
    map: data.map,
  });

  const generateShape = (polygon) =>
    new maps.Polygon(
      polygonStyling({
        map: map,
        path: polygon.geometry,
      })
    );

  const getStartingZoom = useCallback(() => {
    if (defaultZoom) {
      setZoom(defaultZoom);
    } else if (field) {
      setZoom(getZoomLevel(field));
    }
  }, [defaultZoom, field]);

  const updateMapOverlay = useCallback(() => {
    if (map && maps && overlay && overlay.img && overlay.bbox) {
      const { img, bbox } = overlay;

      // create an image and wait for load
      const overlayImage = new Image();
      overlayImage.src = img;

      // if success, add layer to map
      overlayImage.onload = () => {
        const newMapOverlay = new maps.GroundOverlay(img, {
          north: bbox[3],
          south: bbox[1],
          east: bbox[2],
          west: bbox[0],
        });
        // newMapOverlay.setOpacity(.5); // this can be used to set opacity
        newMapOverlay.setMap(map);

        // this can be used to restrict the map to certain bounds - leaving here for future use
        // map.setOptions({ restriction: {
        //   latLngBounds: {
        //     north: bbox[3],
        //     south: bbox[1],
        //     east: bbox[2],
        //     west: bbox[0],
        //   },
        //   strictBounds: true,
        // }});

        // trigger a redraw
        maps.event.trigger(map, "resize");
      };

      // failure is likely, as it may take a second for the image to become available, try again
      overlayImage.onerror = () => {
        setTimeout(() => {
          updateMapOverlay();
        }, 250);
      };
    }
  }, [map, maps, overlay]);

  const addGeneralEventListeners = () => {
    maps.event.addListener(map, "idle", () => {
      updateMapFooterStyles();
    });
  };

  useEffect(() => {
    updateMapOverlay();
  }, [map, maps, overlay]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (map && maps) {
      // add general listeners for all maps
      addGeneralEventListeners();

      // setup for maps with a single shape
      if (geometry) {
        const square = location ? getDefaultSquare(location) : [];
        const geometries = geometry && geometry.geometry ? { geometry: geometry.geometry } : { geometry: square };
        generateShape(geometries);
      }
    }
  }, [map, maps]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!coordinates || coordinates.default) {
      setCoordinates(geometry?.center || field?.location || location);
    }

    getStartingZoom();
  }, [field?.location, geometry?.center, getStartingZoom, location, coordinates]);

  return (
    <GoogleMapReact
      key={`${coordinates.lng.toFixed(1)}-${coordinates.lat.toFixed(1)}`}
      bootstrapURLKeys={{
        key: getGoogleKey(),
        libraries: ["drawing"],
      }}
      defaultCenter={{ lat: 52.52437, lng: 13.41053 }}
      center={coordinates}
      zoom={zoom}
      defaultZoom={15}
      options={(maps) => getMapOptions(maps)}
      yesIWantToUseGoogleMapApiInternals={true}
      onChange={onChange ? (v) => onChange(v) : null}
      onGoogleApiLoaded={({ map, maps }) => {
        // set map and maps globally for ease
        // use hooks to do things with them
        setMap(map);
        setMaps(maps);
      }}
    />
  );
};

export default MapInteractiveWithBoundedOverlay;
